import { useEffect } from 'react';
import { addDays, addYears, format, isAfter, isBefore, subDays } from 'date-fns';

import { Box, FormField, FormHelp, Label, TextButton } from '@zillow/constellation-10';

import type { Lease, Section } from '../../../models/index';
import { LEASE_TERMS } from '../../../constants';
import LeaseTermsListBox from './LeaseTerms/LeaseTermsListBox';
import LeaseTermsEndDate from './LeaseTerms/LeaseTermsEndDate';
import LeaseTermsStartDate from './LeaseTerms/LeaseTermsStartDate';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  hideLegalText?: boolean;
  lease: Lease;
};

export default function LeaseTerms_v5({ section, setSectionValues, hideLegalText, lease }: Props): JSX.Element {
  useEffect(() => {
    const field = section.getField(LEASE_TERMS.FIELD_NAME.END_DATE);
    if (field) {
      field.customValidator = endDateValidator;
    }
  }, []);

  function isSelected(termFieldName: string): boolean {
    return section.getFieldValue(termFieldName) || false;
  }

  function getStartDate(): string {
    return section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function endDateValidator(value: any): boolean {
    const startDate = getStartDate();
    const oneDayAfterStart = addDays(startDate, 1);
    const oneYearAfterStart = addYears(startDate, 1);

    if (
      (startDate && value && isBefore(value, oneDayAfterStart)) ||
      (isAfter(value, oneYearAfterStart) && !hideLegalText)
    ) {
      return false;
    }
    return true;
  }

  function onSetOneYearTermClick(): void {
    const startDate = getStartDate();
    const formattedOneYearLaterEndDate = format(subDays(addYears(startDate, 1), 1), 'MMMM d, yyyy');

    setSectionValues({ [LEASE_TERMS.FIELD_NAME.END_DATE]: formattedOneYearLaterEndDate });
  }

  function setOneYearTermLabel(): string {
    if (
      section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError ||
      !section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE)
    ) {
      return 'Set to 1 year term';
    }
    const startDate = getStartDate();
    const formattedOneYearLaterEndDate = format(subDays(addYears(startDate, 1), 1), 'MMMM d, yyyy');

    return `Set to 1 year term (${formattedOneYearLaterEndDate})`;
  }

  return (
    <>
      <LeaseTermsListBox
        section={section}
        setSectionValues={setSectionValues}
        lease={lease}
        legendText="What are your lease terms?"
      />
      {(isSelected(LEASE_TERMS.FIELD_NAME.IS_MONTH_TO_MONTH) || isSelected(LEASE_TERMS.FIELD_NAME.IS_FIXED)) && (
        <Box css={{ marginBottom: 'layout.tight' }}>
          <FormField
            required
            css={{ marginBottom: 'layout.tighter' }}
            label={<Label>{LEASE_TERMS.LABEL.START_DATE}</Label>}
            error={section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError}
            control={<LeaseTermsStartDate section={section} setSectionValues={setSectionValues} lease={lease} />}
            description={
              section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError && (
                <FormHelp>{section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).getErrorMessage()}</FormHelp>
              )
            }
          />
          {!isSelected(LEASE_TERMS.FIELD_NAME.IS_MONTH_TO_MONTH) && (
            <Box css={{ marginTop: 'layout.tight' }}>
              <FormField
                required
                css={{ marginBottom: 'layout.tighter' }}
                label={<Label>{LEASE_TERMS.LABEL.END_DATE}</Label>}
                error={section.getField(LEASE_TERMS.FIELD_NAME.END_DATE).hasError}
                control={
                  <LeaseTermsEndDate
                    restrictToOneYear
                    hideLegalText={hideLegalText}
                    section={section}
                    setSectionValues={setSectionValues}
                    lease={lease}
                  />
                }
                description={
                  section.getField(LEASE_TERMS.FIELD_NAME.END_DATE).hasError ? (
                    <FormHelp>{section.getField(LEASE_TERMS.FIELD_NAME.END_DATE).getErrorMessage()}</FormHelp>
                  ) : (
                    !hideLegalText && (
                      <FormHelp>
                        Under Washington law, any lease with a fixed term of longer than one year must be notarized
                      </FormHelp>
                    )
                  )
                }
              />
              <TextButton
                disabled={
                  section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError ||
                  !section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE)
                }
                onClick={onSetOneYearTermClick}
              >
                {setOneYearTermLabel()}
              </TextButton>
            </Box>
          )}
        </Box>
      )}
    </>
  );
}

LeaseTerms_v5.description = 'Lease Terms for Washington state.';
